import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpDown,
  Bookmark,
  BookmarkX,
  Check,
  Clock3,
  Copy,
  ExternalLink,
  FileText,
  Heart,
  Loader,
  NotebookPen,
  RefreshCw,
  Search,
  Share2,
  Sparkles,
  SquareStack,
  UserRound,
  Users,
  X,
} from "lucide-react";
import FooterNav from "../components/FooterNav";
import PostShareModal from "../components/PostShareModal";
import { createNote, fetchNotes } from "../api/notes";
import { fetchSavedLibrary, removeSavedLibraryItem, type SavedLibraryItem, type SavedLibrarySort, type SavedLibraryType } from "../api/saved";
import type { Post } from "../types";
import { formatBlackspaceDateTime } from "../utils/time";

type Notice = { tone: "ok" | "error"; text: string } | null;

const SORT_OPTIONS: Array<{ id: SavedLibrarySort; label: string; icon: ComponentType<{ size?: number; className?: string }> }> = [
  { id: "saved-newest", label: "Saved newest", icon: Bookmark },
  { id: "item-newest", label: "Newest content", icon: Clock3 },
  { id: "most-loved", label: "Most loved", icon: Heart },
];

const TYPE_OPTIONS: Array<{ id: SavedLibraryType; label: string; icon: ComponentType<{ size?: number; className?: string }> }> = [
  { id: "all", label: "All", icon: SquareStack },
  { id: "posts", label: "Posts", icon: Bookmark },
  { id: "spaces", label: "Spaces", icon: Users },
  { id: "people", label: "People", icon: UserRound },
  { id: "notes", label: "Notes", icon: FileText },
];

function noteTitleForPost(post: Post) {
  const author = post.author?.displayName || post.author?.username || "Anonymous";
  const space = post.space?.name || "Blackspace";
  return `Saved post from ${author} in ${space}`.slice(0, 120);
}

function noteContentForPost(post: Post) {
  const author = post.author?.displayName || post.author?.username || "Anonymous";
  const username = post.author?.username ? `@${post.author.username}` : "anonymous";
  const space = post.space?.name || "Blackspace";
  const created = post.createdAt ? formatBlackspaceDateTime(post.createdAt) : "";
  return [
    `Source: blackspace-post:${post.id}`,
    `Author: ${author} (${username})`,
    `Space: ${space}`,
    created ? `Posted: ${created}` : "",
    "",
    String(post.content || "").trim(),
  ].filter(Boolean).join("\n");
}

function savedLabel(item: SavedLibraryItem) {
  if (item.savedAt) return `Saved ${formatBlackspaceDateTime(item.savedAt)}`;
  if (item.sortDate) return `Updated ${formatBlackspaceDateTime(item.sortDate)}`;
  return "Saved";
}

function itemIcon(kind: SavedLibraryItem["kind"]) {
  if (kind === "space") return Users;
  if (kind === "person") return UserRound;
  if (kind === "note") return FileText;
  return Bookmark;
}

export default function Saved() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as { from?: string; reopenBlackspaceMenuOnBack?: boolean } | null;
  const originRef = useRef<string | null>(routeState?.from || null);
  const reopenMenuOnBackRef = useRef(Boolean(routeState?.reopenBlackspaceMenuOnBack));
  const abortRef = useRef<AbortController | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const [items, setItems] = useState<SavedLibraryItem[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [totals, setTotals] = useState<Record<SavedLibraryType, number>>({ all: 0, posts: 0, spaces: 0, people: 0, notes: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [sharePost, setSharePost] = useState<Post | null>(null);
  const [noteSavingId, setNoteSavingId] = useState<string | null>(null);
  const [noteSavedIds, setNoteSavedIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [sortMode, setSortMode] = useState<SavedLibrarySort>("saved-newest");
  const [contentType, setContentType] = useState<SavedLibraryType>("all");

  const savedCount = useMemo(() => totals[contentType] ?? totals.all ?? items.length, [contentType, items.length, totals]);
  const hasFilters = Boolean(activeQuery.trim()) || sortMode !== "saved-newest" || contentType !== "all";
  const goBack = useCallback(() => {
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (originRef.current && originRef.current !== currentRoute) {
      navigate(originRef.current, { state: reopenMenuOnBackRef.current ? { reopenBlackspaceMenu: true } : undefined });
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/home");
  }, [location.hash, location.pathname, location.search, navigate]);

  const showNotice = useCallback((tone: "ok" | "error", text: string) => {
    setNotice({ tone, text });
    window.setTimeout(() => setNotice(null), tone === "error" ? 2600 : 1600);
  }, []);

  const load = useCallback(async (page = 1, append = false) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const result = await fetchSavedLibrary({
        page,
        limit: 12,
        q: activeQuery,
        type: contentType,
        sort: sortMode,
        signal: controller.signal,
      });
      setItems((current) => (append ? [...current, ...result.items] : result.items));
      setTotals(result.totals);
      setNextPage(result.nextPage);
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        setError(err?.message || "Saved could not be loaded.");
      }
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        setLoadingMore(false);
        abortRef.current = null;
      }
    }
  }, [activeQuery, contentType, sortMode]);

  useEffect(() => {
    load(1, false);
    return () => abortRef.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!searchOpen) return;
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, [searchOpen]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !nextPage || loading || loadingMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        load(nextPage, true);
      }
    }, { rootMargin: "280px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [load, loading, loadingMore, nextPage]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest("[data-saved-popover]")) setSortOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setActiveQuery(query.trim()), 240);
    return () => window.clearTimeout(timer);
  }, [query]);

  const refresh = () => load(1, false);

  const clearSearch = () => {
    setQuery("");
    setActiveQuery("");
    setSearchOpen(false);
  };

  const removeSaved = async (item: SavedLibraryItem) => {
    if (removingId) return;
    setRemovingId(`${item.kind}:${item.id}`);
    try {
      await removeSavedLibraryItem(item.kind, item.id);
      setItems((current) => current.filter((entry) => !(entry.kind === item.kind && entry.id === item.id)));
      setTotals((current) => {
        const key = item.kind === "person" ? "people" : `${item.kind}s` as SavedLibraryType;
        return {
          ...current,
          all: Math.max(0, (current.all || 0) - 1),
          [key]: Math.max(0, (current[key] || 0) - 1),
        };
      });
      showNotice("ok", item.kind === "note" ? "Moved to Vault trash." : "Removed from Saved.");
    } catch (err: any) {
      showNotice("error", err?.message || "Could not remove this item.");
    } finally {
      setRemovingId(null);
    }
  };

  const copyItem = async (item: SavedLibraryItem) => {
    try {
      const text = item.post?.content || [item.title, item.subtitle, item.content].filter(Boolean).join("\n");
      await navigator.clipboard.writeText(String(text || ""));
      setCopiedId(`${item.kind}:${item.id}`);
      showNotice("ok", "Copied.");
      window.setTimeout(() => setCopiedId(null), 1200);
    } catch (err: any) {
      showNotice("error", err?.message || "Could not copy.");
    }
  };

  const shareItem = async (item: SavedLibraryItem) => {
    if (item.post) {
      setSharePost(item.post);
      return;
    }
    try {
      const link = `${window.location.origin}${item.href || "/saved"}`;
      await navigator.clipboard.writeText(link);
      showNotice("ok", "Link copied.");
    } catch (err: any) {
      showNotice("error", err?.message || "Could not share this item.");
    }
  };

  const savePostAsNote = async (post: Post) => {
    if (noteSavingId || noteSavedIds.has(post.id)) return;
    setNoteSavingId(post.id);
    try {
      const marker = `blackspace-post:${post.id}`;
      const existing = await fetchNotes({ search: marker, limit: 1 });
      if (existing.notes.length > 0) {
        setNoteSavedIds((current) => new Set(current).add(post.id));
        showNotice("ok", "Already in Vault.");
        return;
      }
      await createNote({
        title: noteTitleForPost(post),
        content: noteContentForPost(post),
        type: "text",
        tags: ["saved-post", post.space?.name || "blackspace"].filter(Boolean),
      });
      setNoteSavedIds((current) => new Set(current).add(post.id));
      showNotice("ok", "Saved as a Vault note.");
    } catch (err: any) {
      showNotice("error", err?.message || "Could not save as note.");
    } finally {
      setNoteSavingId(null);
    }
  };

  const openItem = (item: SavedLibraryItem) => {
    if (item.kind === "note") {
      navigate("/notes", { state: { noteId: item.id, from: "/saved", label: "Saved" } });
      return;
    }
    navigate(item.href || "/saved", { state: { from: "/saved", label: "Saved" } });
  };

  const savedKindLabel = (kind: SavedLibraryItem["kind"]) => ({
    note: "Note",
    person: "Profile",
    post: "Post",
    space: "Space",
  }[kind]);

  const renderCompactItem = (item: SavedLibraryItem) => {
    const Icon = itemIcon(item.kind);
    const busy = removingId === `${item.kind}:${item.id}`;
    return (
      <article key={`${item.kind}:${item.id}`} className="bs-saved-item-card group">
        <button
          type="button"
          onClick={() => openItem(item)}
          className="bs-saved-card-open"
          title="Open"
          aria-label="Open"
        >
          <ExternalLink size={16} />
        </button>

        <div className="flex items-start gap-3 pr-11">
          <div
            className="bs-saved-card-icon"
            style={item.accentColor || item.color ? { color: item.accentColor || item.color } : undefined}
          >
            <Icon size={20} />
          </div>
          <div className="min-w-0">
            <p className="bs-saved-card-kicker">
              <span>{savedKindLabel(item.kind)}</span>
              <span>{savedLabel(item)}</span>
            </p>
            <h2 className="bs-saved-card-title">{item.title}</h2>
            {item.subtitle && <p className="mt-1 truncate text-sm font-semibold text-neutral-400">{item.subtitle}</p>}
            {item.content && <p className="bs-saved-card-copy">{item.content}</p>}
          </div>
        </div>

        <div className="bs-saved-card-actions">
          <button type="button" onClick={() => shareItem(item)} className="bs-saved-card-action text-emerald-100" title="Share" aria-label="Share">
            <Share2 size={16} />
          </button>
          <button type="button" onClick={() => copyItem(item)} className="bs-saved-card-action text-neutral-200" title={copiedId === `${item.kind}:${item.id}` ? "Copied" : "Copy"} aria-label={copiedId === `${item.kind}:${item.id}` ? "Copied" : "Copy"}>
            {copiedId === `${item.kind}:${item.id}` ? <Check size={16} className="text-emerald-300" /> : <Copy size={16} className="text-neutral-400" />}
          </button>
          <button type="button" onClick={() => removeSaved(item)} disabled={busy} className="bs-saved-card-action text-rose-100 disabled:cursor-wait disabled:opacity-60" title={item.kind === "note" ? "Move note to Vault trash" : "Remove from Saved"} aria-label={item.kind === "note" ? "Move note to Vault trash" : "Remove from Saved"}>
            {busy ? <Loader size={16} className="animate-spin" /> : <BookmarkX size={16} />}
          </button>
        </div>
      </article>
    );
  };

  return (
    <div className="bs-page bs-saved-page flex min-h-screen flex-col">
      <main className="bs-saved-main mx-auto flex w-full max-w-5xl flex-1 flex-col gap-4 px-4 pb-28 pt-5 sm:px-8 lg:px-10">
        <header className="bs-saved-hero relative overflow-hidden rounded-[1.35rem] border border-cyan-300/15 bg-black/55 p-4 shadow-[0_0_70px_rgba(34,211,238,0.08)] sm:p-5">
          <div className="bs-saved-hero-glow pointer-events-none absolute inset-0 opacity-80" />

          <div className="relative flex items-center justify-between gap-3">
            <button type="button" onClick={goBack} className="bs-saved-header-action" aria-label="Back">
              <ArrowLeft size={19} />
            </button>

            <div className="min-w-0 flex-1 px-1">
              <h1 className="bs-message-page-title truncate">Saved</h1>
            </div>

            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setSearchOpen((value) => !value)} className={`bs-saved-header-action ${searchOpen || query.trim() ? "is-active" : ""}`} aria-label="Search saved library" aria-pressed={searchOpen}>
                <Search size={18} />
              </button>
              <div className="relative" data-saved-popover>
                <button type="button" onClick={() => setSortOpen((value) => !value)} className={`bs-saved-header-action ${sortOpen || hasFilters ? "is-active" : ""}`} aria-label="Sort saved library" aria-pressed={sortOpen}>
                  <ArrowUpDown size={18} />
                </button>
                {sortOpen && (
                  <div className="bs-saved-popover absolute right-0 top-14 z-20 w-72 rounded-2xl border border-cyan-300/15 bg-black/90 p-3 shadow-[0_24px_70px_rgba(0,0,0,0.5)] backdrop-blur-xl">
                    <p className="px-2 pb-2 text-[11px] font-black uppercase tracking-[0.22em] text-neutral-500">Sort</p>
                    <div className="grid gap-2">
                      {SORT_OPTIONS.map((option) => {
                        const Icon = option.icon;
                        const active = sortMode === option.id;
                        return (
                          <button key={option.id} type="button" onClick={() => setSortMode(option.id)} className={`flex items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-black transition ${active ? "bg-cyan-300/12 text-cyan-100 shadow-[0_0_22px_rgba(34,211,238,0.12)]" : "text-neutral-400 hover:bg-white/5 hover:text-white"}`}>
                            <Icon size={16} className={active ? "text-cyan-200" : "text-neutral-500"} />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="px-2 pb-2 pt-4 text-[11px] font-black uppercase tracking-[0.22em] text-neutral-500">Show</p>
                    <div className="grid grid-cols-5 gap-2">
                      {TYPE_OPTIONS.map((option) => {
                        const Icon = option.icon;
                        const active = contentType === option.id;
                        return (
                          <button key={option.id} type="button" onClick={() => setContentType(option.id)} className={`grid h-12 place-items-center rounded-xl text-xs font-black transition ${active ? "bg-cyan-300/12 text-cyan-100 shadow-[0_0_22px_rgba(34,211,238,0.12)]" : "text-neutral-500 hover:bg-white/5 hover:text-white"}`} title={option.label} aria-label={option.label}>
                            <Icon size={17} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <button type="button" onClick={refresh} disabled={loading} className="bs-saved-header-action disabled:cursor-wait disabled:opacity-60" title="Refresh" aria-label="Refresh Saved">
                <RefreshCw size={17} className={loading ? "animate-spin text-cyan-300" : ""} />
              </button>
            </div>
          </div>

          {searchOpen && (
            <div className="bs-saved-search relative mt-4 flex items-center gap-2 rounded-2xl bg-black/70 px-4 py-3 ring-1 ring-cyan-300/15 focus-within:ring-cyan-300/45">
              <Search size={18} className="text-cyan-300" />
              <input ref={searchInputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search Saved..." className="min-w-0 flex-1 bg-transparent text-base font-semibold text-white outline-none placeholder:text-neutral-600" />
              {query && (
                <button type="button" onClick={clearSearch} className="text-neutral-500 transition hover:text-white" aria-label="Clear saved search">
                  <X size={18} />
                </button>
              )}
            </div>
          )}

          <div className="relative mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="bs-saved-count">
              <Bookmark size={15} />
              <span>{savedCount}</span>
              {activeQuery && <span className="text-cyan-200"> matching "{activeQuery}"</span>}
            </p>
          </div>
        </header>

        {notice && (
          <div className={`rounded-2xl px-4 py-3 text-sm font-black shadow-[0_18px_50px_rgba(0,0,0,0.25)] ${notice.tone === "ok" ? "border border-cyan-300/20 bg-cyan-300/10 text-cyan-100" : "border border-rose-400/25 bg-rose-500/10 text-rose-100"}`}>
            {notice.text}
          </div>
        )}

        {error && <div className="rounded-2xl border border-rose-400/25 bg-rose-500/10 p-4 text-sm font-semibold text-rose-100">{error}</div>}

        {loading ? (
          <section className="grid min-h-72 place-items-center rounded-[1.35rem] bg-black/35 ring-1 ring-white/5">
            <div className="flex items-center gap-3 text-neutral-400">
              <Loader size={18} className="animate-spin text-cyan-300" />
              Loading Saved
            </div>
          </section>
        ) : items.length === 0 ? (
          <section className="bs-saved-empty relative grid min-h-80 place-items-center overflow-hidden rounded-[1.35rem] bg-black/35 p-8 text-center ring-1 ring-white/5">
            <div className="bs-saved-empty-stars" aria-hidden="true" />
            <div className="relative">
              <div className="bs-saved-empty-icon">
                <Bookmark size={36} />
                <Sparkles size={18} className="absolute" />
              </div>
              <h2 className="mt-5 text-2xl font-black text-white">{hasFilters ? "No saved matches" : "Nothing saved yet"}</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-neutral-500">
                {hasFilters ? "Try a softer search, switch Show back to All, or return Sort to Saved newest." : "Save posts now. Spaces, people, and private Vault notes are ready to appear here as your library grows."}
              </p>
              <button type="button" onClick={hasFilters ? () => { setQuery(""); setActiveQuery(""); setSortMode("saved-newest"); setContentType("all"); } : () => navigate("/discover")} className="bs-saved-empty-cta">
                <Bookmark size={15} />
                {hasFilters ? "Clear view" : "Start saving"}
              </button>
            </div>
          </section>
        ) : (
          <section className="grid gap-3">
            {items.map((item) => item.post ? (
              <article key={`${item.kind}:${item.id}`} className="bs-saved-item-card group">
                <button type="button" onClick={() => openItem(item)} className="bs-saved-card-open" title="Open post" aria-label="Open post">
                  <ExternalLink size={16} />
                </button>

                <div className="flex items-start gap-3 pr-11">
                  <div className="bs-saved-card-icon">
                    <Bookmark size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="bs-saved-card-kicker">
                      <span>Post</span>
                      <span>{savedLabel(item)}</span>
                    </p>
                    <h2 className="bs-saved-card-title">{item.title}</h2>
                    {item.content && <p className="bs-saved-card-copy">{item.content}</p>}
                  </div>
                </div>

                <div className="bs-saved-card-actions">
                  <button type="button" onClick={() => shareItem(item)} className="bs-saved-card-action text-emerald-100" title="Share" aria-label="Share">
                    <Share2 size={16} />
                  </button>
                  <button type="button" onClick={() => savePostAsNote(item.post!)} disabled={noteSavingId === item.post.id || noteSavedIds.has(item.post.id)} className="bs-saved-card-action text-cyan-100 disabled:cursor-not-allowed disabled:opacity-60" title={noteSavedIds.has(item.post.id) ? "Already in Vault" : "Save as note"} aria-label={noteSavedIds.has(item.post.id) ? "Already in Vault" : "Save as note"}>
                    {noteSavedIds.has(item.post.id) ? <Check size={16} /> : noteSavingId === item.post.id ? <Loader size={16} className="animate-spin" /> : <NotebookPen size={16} />}
                  </button>
                  <button type="button" onClick={() => copyItem(item)} className="bs-saved-card-action text-neutral-200" title={copiedId === `${item.kind}:${item.id}` ? "Copied" : "Copy"} aria-label={copiedId === `${item.kind}:${item.id}` ? "Copied" : "Copy"}>
                    {copiedId === `${item.kind}:${item.id}` ? <Check size={16} className="text-emerald-300" /> : <Copy size={16} className="text-neutral-400" />}
                  </button>
                  <button type="button" onClick={() => removeSaved(item)} disabled={removingId === `${item.kind}:${item.id}`} className="bs-saved-card-action text-rose-100 disabled:cursor-wait disabled:opacity-60" title="Remove from Saved" aria-label="Remove from Saved">
                    {removingId === `${item.kind}:${item.id}` ? <Loader size={16} className="animate-spin" /> : <BookmarkX size={16} />}
                  </button>
                </div>
              </article>
            ) : renderCompactItem(item))}
          </section>
        )}

        <div ref={sentinelRef} className="h-8" />
        {loadingMore && (
          <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-cyan-300/10 px-4 py-2 text-sm font-black text-cyan-100">
            <Loader size={15} className="animate-spin" />
            Loading more
          </div>
        )}
      </main>

      {sharePost && <PostShareModal post={sharePost} open={Boolean(sharePost)} onClose={() => setSharePost(null)} />}
      <FooterNav />
    </div>
  );
}
